import { useState, useEffect, useRef } from 'react';
import { apiRequest } from '../services/api-client';
import { pushTokenLogger, type LogEntry } from '../utils/pushTokenLogger';

export default function NotificationTestPage() {
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Prenumerera på loggar från global logger
  useEffect(() => {
    const unsubscribe = pushTokenLogger.subscribe((newLogs) => {
      setLogs(newLogs);
    });

    return unsubscribe;
  }, []);

  // Listen for diagnostics from React Native
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.type === 'DIAGNOSTICS_RESPONSE') {
          pushTokenLogger.log('📱 Received diagnostics from React Native', 'success');
          pushTokenLogger.log(`Platform: ${data.platform} ${data.platformVersion}`, 'info');
          pushTokenLogger.log(`Has Expo Push Token: ${data.hasExpoPushToken ? 'YES' : 'NO'}`, data.hasExpoPushToken ? 'success' : 'error');
          pushTokenLogger.log(`Token Preview: ${data.expoPushTokenPreview || 'N/A'}`, 'info');
          pushTokenLogger.log(`Has ProjectId: ${data.hasProjectId ? 'YES' : 'NO'}`, data.hasProjectId ? 'success' : 'error');
          pushTokenLogger.log(`ProjectId: ${data.projectId || 'N/A'}`, 'info');
        }
      } catch (e) {
        // Not a diagnostics message
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Auto-scroll till botten när nya loggar kommer
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const clearLogs = () => pushTokenLogger.clear();

  const checkTokenManually = () => {
    pushTokenLogger.log('🔍 Manual token check triggered', 'info');
    
    // Check window
    const windowToken = (window as any).expoPushToken;
    pushTokenLogger.log(`📱 window.expoPushToken: ${windowToken ? windowToken.substring(0, 40) + '...' : 'FINNS INTE'}`, windowToken ? 'success' : 'warning');
    
    // Check localStorage
    try {
      const storedToken = localStorage.getItem('expoPushToken');
      pushTokenLogger.log(`💾 localStorage.expoPushToken: ${storedToken ? storedToken.substring(0, 40) + '...' : 'FINNS INTE'}`, storedToken ? 'success' : 'warning');
    } catch (e) {
      pushTokenLogger.log(`❌ Fel vid läsning av localStorage: ${e}`, 'error');
    }
    
    // Check if we're in WebView
    const isWebView = !!(window as any).ReactNativeWebView;
    pushTokenLogger.log(`📲 Kör i React Native WebView: ${isWebView ? 'JA' : 'NEJ'}`, isWebView ? 'success' : 'warning');
    
    // Request diagnostics from React Native if in WebView
    if (isWebView) {
      pushTokenLogger.log('📡 Requesting diagnostics from React Native...', 'info');
      try {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify({
          type: 'REQUEST_DIAGNOSTICS'
        }));
      } catch (e) {
        pushTokenLogger.log(`❌ Failed to request diagnostics: ${e}`, 'error');
      }
    }
  };

  const fetchDebugInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiRequest<any>('/notifications/debug');
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch debug info');
      }
      
      setDebugInfo(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch debug info');
    } finally {
      setLoading(false);
    }
  };

  const sendTestNotification = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiRequest<any>('/notifications/test', {
        method: 'POST',
      });
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to send test notification');
      }
      
      setTestResult(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send test notification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white p-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Push Notification Debug</h1>

        {/* Push Token Debug Log */}
        <div className="mb-6 bg-gray-900 rounded-lg border border-gray-700">
          <div className="p-4 border-b border-gray-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <span>📋</span>
              <span>Live Debug Log</span>
              <span className="text-xs text-gray-500 font-normal">
                ({logs.length} händelser)
              </span>
            </h2>
            <button
              onClick={clearLogs}
              className="text-xs px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
            >
              Rensa
            </button>
          </div>
          <div className="h-64 overflow-y-auto p-3 font-mono text-xs space-y-1 bg-black/30">
            {logs.length === 0 ? (
              <div className="text-gray-500 text-center py-8">
                Väntar på push token-händelser...
              </div>
            ) : (
              logs.map((log, idx) => (
                <div 
                  key={idx} 
                  className={`flex gap-2 ${
                    log.level === 'error' ? 'text-red-400' :
                    log.level === 'warning' ? 'text-yellow-400' :
                    log.level === 'success' ? 'text-green-400' :
                    'text-gray-300'
                  }`}
                >
                  <span className="text-gray-600 shrink-0">{log.timestamp}</span>
                  <span className="soft-wrap">{log.message}</span>
                </div>
              ))
            )}
            <div ref={logsEndRef} />
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-900/50 border border-red-700 rounded p-4">
            <p className="text-red-200">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {/* Debug Info Button */}
          <button
            onClick={fetchDebugInfo}
            disabled={loading}
            className="w-full bg-[#945123] hover:bg-[#7a3f1d] disabled:bg-gray-600 text-white font-semibold py-3 px-4 rounded transition-colors"
          >
            {loading ? 'Loading...' : 'Show Debug Info'}
          </button>
          {/* Manual Token Check Button */}
          <button
            onClick={checkTokenManually}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded transition-colors"
          >
            🔍 Check Token Now (Manual)
          </button>
          {/* Test Notification Button */}
          <button
            onClick={sendTestNotification}
            disabled={loading}
            className="w-full bg-[#67724e] hover:bg-[#545d3e] disabled:bg-gray-600 text-white font-semibold py-3 px-4 rounded transition-colors"
          >
            {loading ? 'Sending...' : 'Send Test Notification'}
          </button>

          {/* Error Display */}
          {error && (
            <div className="bg-red-900/50 border border-red-700 rounded p-4">
              <p className="text-red-200 font-semibold">Error:</p>
              <p className="text-red-300">{error}</p>
            </div>
          )}

          {/* Debug Info Display */}
          {debugInfo && (
            <div className="bg-gray-800 rounded p-4 space-y-3">
              <h2 className="text-xl font-semibold text-[#945123]">Debug Information</h2>
              
              <div>
                <p className="text-gray-400 text-sm">User ID:</p>
                <p className="font-mono text-sm">{debugInfo.userId}</p>
              </div>

              <div>
                <p className="text-gray-400 text-sm">Email:</p>
                <p className="font-mono text-sm">{debugInfo.email}</p>
              </div>

              {debugInfo.displayName && (
                <div>
                  <p className="text-gray-400 text-sm">Display Name:</p>
                  <p className="font-mono text-sm">{debugInfo.displayName}</p>
                </div>
              )}

              <div>
                <p className="text-gray-400 text-sm">Family ID:</p>
                <p className="font-mono text-sm">{debugInfo.familyId}</p>
              </div>

              <div>
                <p className="text-gray-400 text-sm font-semibold">Push Token Status:</p>
                <div className="ml-4 space-y-1">
                  <p className="text-sm">
                    <span className="text-gray-400">Exists:</span>{' '}
                    <span className={debugInfo.pushToken.exists ? 'text-green-400' : 'text-red-400'}>
                      {debugInfo.pushToken.exists ? '✓ Yes' : '✗ No'}
                    </span>
                  </p>
                  {debugInfo.pushToken.exists && (
                    <>
                      <p className="text-sm">
                        <span className="text-gray-400">Valid Format:</span>{' '}
                        <span className={debugInfo.pushToken.isValidFormat ? 'text-green-400' : 'text-red-400'}>
                          {debugInfo.pushToken.isValidFormat ? '✓ Yes' : '✗ No'}
                        </span>
                      </p>
                      <p className="text-sm">
                        <span className="text-gray-400">Preview:</span>{' '}
                        <span className="font-mono text-xs">{debugInfo.pushToken.preview}</span>
                      </p>
                      <p className="text-sm">
                        <span className="text-gray-400">Length:</span> {debugInfo.pushToken.length}
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div>
                <p className="text-gray-400 text-sm font-semibold">
                  Notification Settings ({debugInfo.notificationSettings.summary}):
                </p>
                <div className="ml-4 grid grid-cols-2 gap-2 mt-2">
                  {Object.entries(debugInfo.notificationSettings)
                    .filter(([key]) => key !== 'summary')
                    .map(([key, value]) => (
                      <div key={key} className="text-sm">
                        <span className="text-gray-400">{key}:</span>{' '}
                        <span className={value ? 'text-green-400' : 'text-red-400'}>
                          {value ? '✓' : '✗'}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              {debugInfo.recommendations && debugInfo.recommendations.length > 0 && (
                <div>
                  <p className="text-gray-400 text-sm font-semibold">Recommendations:</p>
                  <ul className="ml-4 mt-2 space-y-1">
                    {debugInfo.recommendations.map((rec: string, idx: number) => (
                      <li key={idx} className="text-sm text-yellow-300">
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Test Result Display */}
          {testResult && (
            <div className={`rounded p-4 ${testResult.success ? 'bg-green-900/50 border border-green-700' : 'bg-red-900/50 border border-red-700'}`}>
              <h2 className="text-xl font-semibold mb-2">
                {testResult.success ? '✓ Success!' : '✗ Failed'}
              </h2>
              <p className={testResult.success ? 'text-green-200' : 'text-red-200'}>
                {testResult.message}
              </p>
              {testResult.sentTo && (
                <p className="text-sm mt-2 text-gray-300">Sent to: {testResult.sentTo}</p>
              )}
              {testResult.tokenPreview && (
                <p className="text-sm mt-1 text-gray-300 font-mono text-xs">
                  Token: {testResult.tokenPreview}
                </p>
              )}
              {testResult.error && (
                <p className="text-sm mt-2 text-red-300">Error: {testResult.error}</p>
              )}
            </div>
          )}

          <div className="bg-gray-800 rounded p-4 mt-6">
            <h3 className="font-semibold mb-2">How to use:</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-300">
              <li>Click "Show Debug Info" to see your push token status and settings</li>
              <li>Check the recommendations for any issues</li>
              <li>Click "Send Test Notification" to send a test push notification</li>
              <li>You should receive the notification on this device within a few seconds</li>
            </ol>
          </div>

          {/* Android Notification Channels Info */}
          <div className="bg-gray-800 rounded p-4 mt-6">
            <h3 className="font-semibold mb-3 text-[#945123]">📱 Android Notification Channels</h3>
            <p className="text-sm text-gray-400 mb-4">Följande notifikationskanaler används i Android-appen:</p>
            <div className="space-y-3">
              <div className="bg-gray-700 rounded p-3">
                <p className="font-semibold text-sm">default - Standard</p>
                <p className="text-xs text-gray-400">Importance: MAX | Används för allmänna meddelanden</p>
              </div>
              <div className="bg-gray-700 rounded p-3">
                <p className="font-semibold text-sm">family-chat - Familjechatten</p>
                <p className="text-xs text-gray-400">Importance: HIGH | Meddelanden från familjechatten</p>
              </div>
              <div className="bg-gray-700 rounded p-3">
                <p className="font-semibold text-sm">group-invitations - Gruppinbjudningar</p>
                <p className="text-xs text-gray-400">Importance: HIGH | När du bjuds in till en grupp</p>
              </div>
              <div className="bg-gray-700 rounded p-3">
                <p className="font-semibold text-sm">group-activity - Gruppaktivitet</p>
                <p className="text-xs text-gray-400">Importance: DEFAULT | Ny aktivitet i grupper (anslagstavla, kalender, wiki)</p>
              </div>
              <div className="bg-gray-700 rounded p-3">
                <p className="font-semibold text-sm">group-requests - Gruppansökningar</p>
                <p className="text-xs text-gray-400">Importance: HIGH | Medlemsansökningar till dina grupper</p>
              </div>
              <div className="bg-gray-700 rounded p-3">
                <p className="font-semibold text-sm">group-decisions - Gruppbeslut</p>
                <p className="text-xs text-gray-400">Importance: HIGH | När din ansökan godkänns/avvisas</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              💡 Tips: I Android-inställningar kan du justera varje kanal individuellt (ljud, vibration, prioritet)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
